import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import Table from '../components/common/Table';
import { getTeamProfile } from '../services/public-api';
import './TeamProfilePage.css';

/**
 * TeamProfilePage
 * Shows team profile with roster and team stats
 * T075: Team profile page with roster and team stats
 */
const TeamProfilePage = () => {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (teamId) {
      fetchTeamProfile();
    }
  }, [teamId]);

  const fetchTeamProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getTeamProfile(teamId);
      setTeam(data.team);
      setPlayers(data.players || []);
      setStats(data.stats);
      setRecentMatches(data.recentMatches || []);
    } catch (err) {
      setError(err.message || 'Failed to load team profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Loading size="large" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage
          message={error}
          variant="error"
          onRetry={fetchTeamProfile}
        />
      </Container>
    );
  }

  if (!team) {
    return (
      <Container>
        <div className="not-found">
          <h1>Team Not Found</h1>
          <p>The team you're looking for doesn't exist.</p>
        </div>
      </Container>
    );
  }

  const formatPercentage = (value) => {
    return value ? `${value.toFixed(1)}%` : '0.0%';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Player roster columns
  const playerColumns = [
    { key: 'name', label: 'Player', align: 'left' },
  ];

  const playerData = players.map((player) => ({
    name: (
      <Link to={`/players/${player.playerId}`} className="player-link">
        {player.name}
      </Link>
    ),
  }));

  // Recent matches columns
  const matchColumns = [
    { key: 'date', label: 'Date', align: 'left' },
    { key: 'opponent', label: 'Opponent', align: 'left' },
    { key: 'score', label: 'Score', align: 'center' },
    { key: 'result', label: 'Result', align: 'center' },
  ];

  const matchData = recentMatches.map((match) => {
    const isTeam1 = match.team1Id === teamId;
    const ourScore = isTeam1 ? match.team1Score : match.team2Score;
    const theirScore = isTeam1 ? match.team2Score : match.team1Score;
    const won = ourScore > theirScore;

    return {
      date: formatDate(match.matchDate),
      opponent: match.opponentName || 'Unknown',
      score: `${ourScore} - ${theirScore}`,
      result: (
        <span className={`result-badge ${won ? 'win' : 'loss'}`}>
          {won ? 'Win' : 'Loss'}
        </span>
      ),
    };
  });

  return (
    <Container>
      <div className="team-profile-page">
        <div className="page-header">
          {team.competitionId && (
            <Link to={`/competitions/${team.competitionId}`} className="back-link">
              ← Back to Competition
            </Link>
          )}
          <h1>{team.name}</h1>
          {team.divisionName && (
            <p className="division-name">{team.divisionName}</p>
          )}
        </div>

        {stats && (
          <div className="team-stats">
            <h2>Team Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-label">Matches Played</span>
                <span className="stat-value">{stats.matchesPlayed}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Matches Won</span>
                <span className="stat-value">{stats.matchesWon}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Matches Lost</span>
                <span className="stat-value">{stats.matchesLost}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Games Won</span>
                <span className="stat-value">{stats.gamesWon}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Games Lost</span>
                <span className="stat-value">{stats.gamesLost}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Win Percentage</span>
                <span className="stat-value">{formatPercentage(stats.winPercentage)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="team-roster">
          <h2>Team Roster</h2>
          {players.length > 0 ? (
            <Table columns={playerColumns} data={playerData} />
          ) : (
            <p className="empty-message">No players on this team yet.</p>
          )}
        </div>

        {recentMatches.length > 0 && (
          <div className="recent-matches">
            <h2>Recent Matches</h2>
            <Table columns={matchColumns} data={matchData} />
          </div>
        )}
      </div>
    </Container>
  );
};

export default TeamProfilePage;
